import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FireIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { fetchRfqs } from '../api/marketplaceApi';

const SupplierRfqs: React.FC = () => {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filter === 'all' ? {} : { status: filter };
      const response = await fetchRfqs(params);
      setRfqs(response.data?.items || response.data || response.items || response || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load RFQs');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n: number) => new Intl.NumberFormat('en-RW', { style: 'currency', currency: 'RWF', minimumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <FireIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">RFQ Opportunities</h1>
              <p className="text-blue-100 mt-1">Browse buyer requests and submit quotations</p>
            </div>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'published', 'closed'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all ${filter === f ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'bg-white text-gray-600 border border-gray-200 hover:border-indigo-300'}`}>
            {f === 'all' ? 'All RFQs' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-indigo-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error}</p>
          <button onClick={fetchData} className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Retry</button>
        </div>
      ) : rfqs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto">
            <DocumentTextIcon className="w-10 h-10 text-indigo-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mt-4">No RFQs available</h3>
          <p className="text-gray-500 mt-2">Buyer requests will appear here when published</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {rfqs.map((rfq: any) => (
            <div key={rfq.id} className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold text-indigo-500 uppercase tracking-wider">{rfq.rfq_number || `RFQ-${rfq.id}`}</p>
                    <h3 className="text-lg font-bold text-gray-900 mt-1">{rfq.title || rfq.description || 'Request for Quotation'}</h3>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
                    rfq.status === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    rfq.status === 'closed' ? 'bg-gray-100 text-gray-600 border-gray-200' :
                    'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>
                    {rfq.status || 'published'}
                  </span>
                </div>

                {rfq.description && <p className="text-sm text-gray-600 mt-3 line-clamp-2">{rfq.description}</p>}

                <div className="mt-4 grid grid-cols-2 gap-3">
                  {rfq.buyer_name && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
                      {rfq.buyer_name}
                    </div>
                  )}
                  {rfq.delivery_location && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPinIcon className="w-4 h-4 text-gray-400" />
                      {rfq.delivery_location}
                    </div>
                  )}
                  {rfq.deadline && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <ClockIcon className="w-4 h-4 text-gray-400" />
                      Due {new Date(rfq.deadline).toLocaleDateString()}
                    </div>
                  )}
                  {rfq.budget && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
                      Budget: {fmt(rfq.budget)}
                    </div>
                  )}
                </div>

                {/* Items */}
                {rfq.items && rfq.items.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs font-bold text-gray-400 uppercase mb-2">Items Requested</p>
                    {rfq.items.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span className="text-gray-700">{item.product_name || item.name}</span>
                        <span className="font-semibold text-gray-900">{item.quantity} {item.unit || 'units'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {rfq.created_at ? `Posted ${new Date(rfq.created_at).toLocaleDateString()}` : ''}
                </span>
                <button
                  onClick={() => navigate(`/marketplace/supplier/rfqs/${rfq.id}`)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm rounded-xl hover:bg-indigo-700 font-bold shadow-md shadow-indigo-200 transition-all"
                >
                  <EyeIcon className="w-4 h-4" />
                  View & Quote
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupplierRfqs;
